import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, setupPasswords, passwordsConfigured } = useAuth();
  const [password, setPassword] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [posPassword, setPosPassword] = useState("");
  const [confirmAdminPassword, setConfirmAdminPassword] = useState("");
  const [confirmPosPassword, setConfirmPosPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showPosPassword, setShowPosPassword] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = passwordsConfigured === false ? "Setup | GeekTickets POS" : "Login | GeekTickets POS";
  }, [passwordsConfigured]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Please enter a password");
      return;
    }

    setLoading(true);
    try {
      const result = await login(password);
      const roleLabel = result.role === "admin" ? "Admin" : result.role === "moto" ? "MOTO" : "POS User";
      toast.success(`Logged in as ${roleLabel}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    
    if (!adminPassword.trim() || !posPassword.trim()) {
      toast.error("Both passwords are required");
      return;
    }

    if (adminPassword.length < 4) {
      toast.error("Admin password must be at least 4 characters");
      return;
    }

    if (posPassword.length < 4) {
      toast.error("POS password must be at least 4 characters");
      return;
    }

    if (adminPassword !== confirmAdminPassword) {
      toast.error("Admin passwords do not match");
      return;
    }

    if (posPassword !== confirmPosPassword) {
      toast.error("POS passwords do not match");
      return;
    }

    if (adminPassword === posPassword) {
      toast.error("Admin and POS passwords must be different");
      return;
    }

    setLoading(true);
    try {
      await setupPasswords(adminPassword, posPassword);
      toast.success("Passwords configured successfully! You can now log in.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set up passwords");
    } finally {
      setLoading(false);
    }
  };

  // Initial setup screen
  if (passwordsConfigured === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
              <img src="/logo.png" alt="GeekTickets" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-2xl font-heading">Initial Setup</CardTitle>
            <CardDescription>
              Set up your admin and POS passwords to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-password">Admin Password</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showAdminPassword ? "text" : "password"}
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Enter admin password"
                    data-testid="setup-admin-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAdminPassword(!showAdminPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showAdminPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-admin-password">Confirm Admin Password</Label>
                <Input
                  id="confirm-admin-password"
                  type={showAdminPassword ? "text" : "password"}
                  value={confirmAdminPassword}
                  onChange={(e) => setConfirmAdminPassword(e.target.value)}
                  placeholder="Confirm admin password"
                  data-testid="setup-confirm-admin-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pos-password">POS Password</Label>
                <div className="relative">
                  <Input
                    id="pos-password"
                    type={showPosPassword ? "text" : "password"}
                    value={posPassword}
                    onChange={(e) => setPosPassword(e.target.value)}
                    placeholder="Enter POS password"
                    data-testid="setup-pos-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPosPassword(!showPosPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPosPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-pos-password">Confirm POS Password</Label>
                <Input
                  id="confirm-pos-password"
                  type={showPosPassword ? "text" : "password"}
                  value={confirmPosPassword}
                  onChange={(e) => setConfirmPosPassword(e.target.value)}
                  placeholder="Confirm POS password"
                  data-testid="setup-confirm-pos-password"
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Password Access Levels:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Admin:</strong> Full access (expires after 10 min)</li>
                  <li><strong>POS:</strong> Payments only (stays logged in)</li>
                </ul>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="setup-submit-btn"
              >
                {loading ? "Setting up..." : "Set Up Passwords"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login screen
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted">
      <Card className="w-full max-w-sm shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
            <img src="/logo.png" alt="GeekTickets" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="text-2xl font-heading">GeekTickets POS</CardTitle>
          <CardDescription>
            Enter your password to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pl-10 pr-10"
                  data-testid="login-password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
